import React from 'react';
import { AppBar, Toolbar, Typography, Box, Chip, Tooltip } from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import Switch from '@mui/material/Switch';

interface HeaderProps {
  toggleTheme: () => void;
  themeMode: 'light' | 'dark';
}

const Header: React.FC<HeaderProps> = ({ toggleTheme, themeMode }) => {
  const textColor = themeMode === 'dark' ? '#000' : '#fff';
  const subTextColor = themeMode === 'dark' ? '#222' : 'rgba(255,255,255,0.7)';
  const subtitle2Color = themeMode === 'dark' ? '#333' : 'rgba(255,255,255,0.85)';
  return (
    <AppBar
      position="static"
      sx={{
        background: (theme) => theme.palette.primary.main,
        boxShadow: 2,
        borderRadius: 0,
        mb: 5,
        px: { xs: 1, md: 3 },
        py: { xs: 0.5, md: 1 },
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          minHeight: { xs: 56, sm: 56 },
          width: '100%',
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StorageIcon sx={{ fontSize: 44, color: textColor, mr: 1 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: textColor, letterSpacing: 1 }}>
                AutoUpgrade Composer
              </Typography>
              <Chip 
                label="STABLE" 
                size="small" 
                sx={{ 
                  fontWeight: 'bold', 
                  ml: 1, 
                  bgcolor: '#2e7d32', // darker green
                  color: '#fff',      // white text
                  fontSize: 13,
                  px: 1.2,
                  borderRadius: 1
                }} 
              />
                              <Typography variant="caption" sx={{ color: subTextColor, ml: 2, fontWeight: 500 }}>
                  v.26.3-2
                </Typography>
            </Box>
            <Typography variant="subtitle2" sx={{ color: subtitle2Color, fontWeight: 400, mt: 0.5 }}>
              Generate your Oracle AutoUpgrade Config Easily
            </Typography>
            <Typography variant="caption" sx={{ color: subTextColor, fontWeight: 400 }}>
             Created by <a href="https://www.linkedin.com/in/viniciusdba/" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}>Marcus Vinicius Miguel Pedro</a> | <a href="https://ace.oracle.com/apex/ace/profile/viniciusdba" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}>Oracle ACE Director ♠</a> | <a href="https://viniciusdba.com.br" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}>Blog</a>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flexGrow: { xs: 0, sm: 1 } }} />
        <Tooltip title={themeMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 0, sm: 2 } }}>
            <Switch
              checked={themeMode === 'light'}
              onChange={toggleTheme}
              color="warning"
              inputProps={{ 'aria-label': 'theme switch' }}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#ffd700',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#ffe066',
                },
                '& .MuiSwitch-track': {
                  backgroundColor: themeMode === 'dark' ? '#e0e0e0' : '#888',
                  opacity: 1,
                },
                '& .MuiSwitch-thumb': {
                  backgroundColor: themeMode === 'dark' ? '#fff' : undefined,
                },
              }}
            />
            {themeMode === 'light' ? (
              <LightbulbIcon sx={{ color: '#ffd700', fontSize: 32, ml: 1, transition: 'color 0.2s', cursor: 'pointer' }} onClick={toggleTheme} />
            ) : (
              <LightbulbOutlinedIcon sx={{ color: '#ffe066', fontSize: 32, ml: 1, transition: 'color 0.2s', cursor: 'pointer' }} onClick={toggleTheme} />
            )}
          </Box>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

export default Header; 